<template>
  <div class="my-franchises-table" data-testid="my-franchises-table">
    <data-table
      :config="datatableConfig"
      :data="rows"
      :refreshButton="{ show: true }"
      @refresh="emit('refresh')"
      @edit-click="emit('edit-click', $event)"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { isSetupSatisfied, hasPayableSetupCharge, type SetupFeeRowLike } from '../../utils/setupFeeStatus'
import { hasLiveSubscription } from '../../utils/subscriptionLiveStatus'
import type { Subscriptions_SubscriptionRow } from '../../contracts/Subscriptions_SubscriptionRow'

const { t } = useI18n()

interface Props {
  rows: Subscriptions_SubscriptionRow[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  refresh: []
  'edit-click': [value: unknown]
  'subscribe-click': [franquia_id: number]
  'pay-click': [franquia_id: number]
  'pay-setup-click': [franquia_id: number]
}>()

function handleButtonClick(payload: { value: unknown; data: Record<string, unknown>; action: unknown }): void {
  if (payload.action === 'subscribe') {
    emit('subscribe-click', payload.data.franchise_id as number)
  }
  if (payload.action === 'pay') {
    emit('pay-click', payload.data.franchise_id as number)
  }
  if (payload.action === 'pay-setup') {
    emit('pay-setup-click', payload.data.franchise_id as number)
  }
}

/**
 * Which rows get the 'Pagar' button — the grace payment door's UI condition.
 *
 * Mirrors POST /subscriptions/pay's own answer to "is anything due?": the three
 * statuses that owe money by construction. 'active' is deliberately absent even
 * though the endpoint accepts an active row whose period already lapsed — the
 * table cannot tell that from here (current_period_end is not on this row's
 * contract), and offering a button the backend may refuse is worse than not
 * offering it. Those franchises reach the door on their next status change.
 *
 * subscription_id is REQUIRED. The synthetic rows /subscriptions/mine appends
 * for a franchise with a staff-granted grace and NO subscription row carry
 * status 'grace' too — but there is no plan, no price and no charge to pay, so
 * the door would answer 'sem_assinatura_viva'. Those rows keep the 'Assinar'
 * button instead, and the two buttons therefore never appear on the same row.
 */
const PAYABLE_STATUSES = ['grace', 'pending']

// Setup predicates come from THE shared module (src/utils/setupFeeStatus) —
// never a local copy: local copies drifted apart and showed 'Assinar' and
// 'Pagar implantação' on the same row (2026-08-20).
const canPay = (row: Record<string, unknown>): boolean =>
  row.subscription_id != null && isSetupSatisfied(row as SetupFeeRowLike) && PAYABLE_STATUSES.includes(row.status as string)

/**
 * Which rows get the 'Assinar' button.
 *
 * The question is about the FRANCHISE, not the row, so it scans every row the
 * franchise has: /subscriptions/mine returns the full history, so a franchise
 * shows one row per subscription it ever had.
 *
 * This used to be `subscription_id == null` — "offer Assinar only where there
 * is no subscription row at all". That silently made cancellation a one-way
 * door: a cancelled subscription leaves its row behind (terminal history is
 * never deleted), so the franchise kept a non-null subscription_id forever, no
 * synthetic row was ever appended for it, and the only affordance that lets a
 * franqueado pick a franchise to subscribe disappeared for good. The backend
 * had no such rule — subscribe() refuses only 'active'/'grace' and supersedes
 * whatever terminal row it finds — so the lockout existed purely in this
 * predicate (2026-08-21).
 *
 * `!canPay` keeps the two doors mutually exclusive: a 'pending' row is not
 * live, but it is a subscription awaiting its first payment, so 'Pagar' is the
 * action that moves it forward, not a second subscription on top of it.
 */
const canOfferSubscribe = (row: Record<string, unknown>): boolean =>
  !canPay(row) &&
  !hasLiveSubscription(props.rows, row.franchise_id as number) &&
  isSetupSatisfied(row as SetupFeeRowLike)

const canPaySetup = (row: Record<string, unknown>): boolean =>
  hasPayableSetupCharge(row as SetupFeeRowLike)



const statusLabel = (status: string | undefined): string => {
  const labels: Record<string, string> = {
    active:     t('Ativa'),
    grace:      t('Carência'),
    cancelled:  t('Cancelada'),
    superseded: t('Substituída'),
    pending:    t('Pendente'),
  }
  if (!status) return t('Sem assinatura')
  return labels[status] ?? status
}

const statusSeverity = (status: string | undefined): string => {
  const map: Record<string, string> = {
    active:     'success',
    grace:      'warn',
    cancelled:  'secondary',
    superseded: 'secondary',
    pending:    'info',
  }
  if (!status) return 'secondary'
  return map[status] ?? 'secondary'
}

// Billing-360 parity (user request 2026-08-20): same column shapes as
// BillingThreeSixtyTable — id+name merged, subscription composite (status
// badge + plan/value/gateway), grace badge, credits, setup-fee badge.
const tableColumns = computed(() => [
  {
    // franchise_id, NOT subscription_id: this anchor is the only way into the
    // user Billing 360, and a never-subscribed franchise (subscription_id
    // null) rendered no anchor at all — leaving exactly the setup-owed rows
    // with no way in (2026-08-20).
    data: 'franchise_id',
    title: t('Franquia'),
    renderType: 'custom_clickable_id',
    mergedColumns: [
      {
        data: 'franchise_name',
        title: t('Nome'),
      },
    ],
  },
  {
    data: 'status',
    title: t('Assinatura'),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => {
      const status = row.status as string | undefined
      return {
        label: statusLabel(status),
        severity: statusSeverity(status),
        tooltip: statusLabel(status),
      }
    },
    mergedColumns: [
      {
        data: 'plan_display',
        title: t('Plano'),
        visible: (row: Record<string, unknown>) => !!row.plan_display && row.plan_display !== '—',
      },
      {
        data: 'price_display',
        title: t('Valor'),
        visible: (row: Record<string, unknown>) => !!row.price_display,
      },
      {
        data: 'provider_display',
        title: t('Gateway'),
        visible: (row: Record<string, unknown>) => !!row.provider_display,
      },
    ],
  },
  {
    data: 'grace_display',
    title: t('Carência'),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => {
      // grace_display is the page's authority-sourced "date (N dias)" string
      // ('—' when no live window) — same fields billing-360 badges off.
      if (row.status === 'grace' && row.grace_display && row.grace_display !== '—') {
        return {
          label: `${t('Ativo')} (${row.grace_display as string})`,
          severity: 'warn',
          tooltip: `${t('Carência ativa até')} ${row.grace_display as string}`,
        }
      }
      return {
        label: t('Sem carência'),
        severity: 'secondary',
        tooltip: t('Sem carência ativa'),
      }
    },
  },
  {
    data: 'credits_display',
    title: t('Créditos'),
    cellRenderer: (params: { value?: unknown; data?: Record<string, unknown> }): string => {
      const text = params?.value != null ? String(params.value) : '—'
      const isCancelled = params?.data?.status === 'cancelled'
      if (isCancelled && text !== '—') {
        return `<span style="text-decoration: line-through; opacity: 0.6;">${text}</span>`
      }
      return text
    },
  },

  {
    data: 'setup_status',
    title: t('Taxa de Implantação'),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => {
      const amount = row.setup_fee_display as string | undefined
      if (isSetupSatisfied(row as SetupFeeRowLike)) {
        if (row.setup_status === 'exempt') {
          return { label: t('Isenta'), severity: 'info', tooltip: t('Taxa de implantação isenta') }
        }
        return {
          label: amount ? `${amount} (${t('Paga')})` : t('Paga'),
          severity: 'success',
          tooltip: t('Taxa de implantação paga'),
        }
      }
      // Owed but WITHOUT an emitted charge: there is nothing the user can pay
      // yet (no 'Pagar implantação' button by design), so the badge must say
      // WHY the row offers no action (user request 2026-08-20).
      if (!hasPayableSetupCharge(row as SetupFeeRowLike)) {
        return {
          label: t('Aguardando emissão'),
          severity: 'warn',
          tooltip: t('Taxa de implantação devida — cobrança ainda não emitida pela equipe'),
        }
      }
      return {
        label: amount ? `${amount} (${t('Pendente')})` : t('Pendente'),
        severity: 'warn',
        tooltip: t('Taxa de implantação pendente'),
      }
    },
  },
  {
    // ONE column for the row's "next step": setup fee is the FIRST gate, so a
    // payable charge shows 'Pagar implantação' and a satisfied, unsubscribed
    // row shows 'Assinar'. The shared predicate makes the two conditions
    // mutually exclusive by construction — same column, never side by side.
    // ('subscription_id' is only the column KEY; handlers read franchise_id
    // off the ROW.)
    data: 'subscription_id',
    title: '',
    renderType: 'action_buttons',
    buttons: [
      {
        text: t('Assinar'),
        icon: 'pi pi-plus',
        class: 'p-button-sm p-button-secondary p-button-outlined',
        action: 'subscribe',
        testId: (row: Record<string, unknown>) => `subscribe-row-${row.franchise_id}`,
        condition: canOfferSubscribe,
      },
      {
        text: t('Pagar implantação'),
        icon: 'pi pi-credit-card',
        class: 'p-button-sm p-button-secondary p-button-outlined',
        action: 'pay-setup',
        testId: (row: Record<string, unknown>) => `pay-setup-row-${row.franchise_id}`,
        condition: canPaySetup,
      },
    ],
  },

  {
    // data MUST stay null: every non-null key above is taken and
    // handleButtonClick reads franchise_id off the ROW, not off the cell value.
    data: null,
    title: '',
    renderType: 'clickable_button',
    buttonText: t('Pagar'),
    buttonIcon: 'pi pi-credit-card',
    buttonClass: 'p-button-sm',
    buttonAction: 'pay',
    buttonTestId: (row: Record<string, unknown>) => `pay-row-${row.franchise_id}`,
    buttonCondition: canPay,
  },
])

const datatableConfig = computed(() => ({
  tableId: 'myFranchisesTable',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 25,
}))
</script>
